import React, { useState, useEffect } from "react";
import { editCusDetails } from "../../services/customerService";
import { updateCustomerDetails } from "../../services/customerService";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import {
  AlertCircle,
  Building,
  Building2,
  Check,
  Loader2,
  User,
  UserPen,
  X,
} from "lucide-react";

const EditCustomerModal = ({ isOpen, onClose, userId, onUpdate }) => {
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [getID, setGetID] = useState("");
  const [customerType, setCustomerType] = useState(null);

  const inputClass = `
w-full
h-11
px-4
rounded-xl
border
border-slate-300
bg-white
text-slate-700
placeholder:text-slate-400
text-sm
outline-none
transition-all
duration-200
focus:border-blue-500
focus:ring-4
focus:ring-blue-100
hover:border-slate-400
`;

  useEffect(() => {
    if (isOpen) fetchCustomerDetails();
  }, [isOpen]);

  const fetchCustomerDetails = async () => {
    setIsLoading(true);
    try {
      const response = await editCusDetails({ user_id: userId });
      setGetID(response.data.user.id);
      setFormData({
        user: response?.data?.user || {},

        company: response?.data?.user?.company_details || {},
      });
    } catch (error) {
      console.error("Failed to fetch customer details:", error);
      showErrorToast("Error fetching customer details.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e, section) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [name]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData?.user?.id) {
        showErrorToast("User ID is missing.");
        setIsSubmitting(false);
        return;
      } else if (!formData?.user?.name) {
        showErrorToast("Name is missing.");
        setIsSubmitting(false);
        return;
      } else if (!formData?.user?.email_address) {
        showErrorToast("Email address is missing.");
        setIsSubmitting(false);
        return;
      } else if (!formData?.user?.mobile_number) {
        showErrorToast("Mobile Number is missing.");
        setIsSubmitting(false);
        return;
      }

      // Prepare the payload
      const payload = {
        user: { ...formData?.user },
        company: {
          ...formData.company,
          company_type_name:
            formData?.company?.company_type_name ||
            formData?.company?.company_type?.name,
        },
      };

      if (customerType) {
        payload.user.type = customerType;
      }

      // Make the API call
      const response = await updateCustomerDetails({
        payload,
        id: getID,
      });

      if (response.status.code === 200) {
        showSuccessToast("Customer details updated successfully!");
        onClose();
        onUpdate();
        return;
      } else if (response.status.code === 422 || response.status === 422) {
        let error = "";
        Object.entries(response?.data?.errors).forEach(([key, val]) => {
          error = val[0];
        });
        showErrorToast(
          response?.data?.message ||
            error ||
            response?.status?.message ||
            response?.data?.message ||
            "Validation failed.",
        );
      }
    } catch (error) {
      console.error("Failed to update customer:", error);
      showErrorToast(
        error.response?.data?.message || "Error updating customer details.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99] p-4">
      <div
        className="
    bg-white
    rounded-3xl px-6
    shadow-[0_20px_60px_rgba(0,0,0,0.15)]
    w-full
    max-w-5xl py-10
    flex
    flex-col
    overflow-hidden
"
      >
        {" "}
        {/* Modal Header */}
        <div className="p-1 border-b flex justify-between items-center">
          <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
            <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
              <User className="w-5 h-5" />
            </div>
            <div className="flex w-full justify-between items-center">
              <div className="min-w-[300px]">
                <h2 className="text-lg font-semibold text-gray-900 tracking-tight">
                  Edit Customer
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Update your customer's profile details
                </p>
              </div>
              {/* Customer Type*/}
              <div className="flex flex-col w-full gap-1">
                <label
                  htmlFor="user_id"
                  className="text-xs font-semibold text-gray-600 uppercase tracking-wide"
                >
                  Customer Type
                </label>
                <select
                  type="text"
                  name="user_id"
                  id="user_id"
                  className="w-full py-2 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50/50 text-gray-800 outline-none transition-all focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400"
                  value={formData?.user?.type}
                  onChange={(e) => setCustomerType(e.target.value)}
                >
                  <option value="">Select Type</option>
                  <option value="INDIVIDUAL">Individual</option>
                  <option value="BUSINESS">Business</option>
                </select>
              </div>
            </div>
          </div>{" "}
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-2xl font-bold"
          >
            &times;
          </button>
        </div>
        {/* Modal Body */}
        <div className="p-1 space-y-1">
          {isLoading ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : (
            formData && (
              <>
                {/* User Details */}
                <div>
                  <h3 className="text-lg text-blue-500 font-semibold mb-2 flex items-center gap-2">
                    <UserPen size={18} />
                    User Details
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-1">
                    {[
                      {
                        label: "Customer Name",
                        name: "name",
                        value: formData?.user?.name || "",
                        required: true,
                      },
                      {
                        label: "Email",
                        name: "email_address",
                        value: formData?.user?.email_address || "",
                        type: "email",
                        required: true,
                      },
                      {
                        label: "Mobile",
                        name: "mobile_number",
                        value: formData?.user?.mobile_number || "",
                        required: true,
                      },
                      {
                        label: "Pin Code",
                        name: "pin_code",
                        value: formData?.user?.pin_code || "",
                      },
                      {
                        label: "Landmark Address",
                        name: "landmark_address",
                        value: formData?.user?.landmark_address || "",
                      },
                      {
                        label: "Post Office",
                        name: "post_office",
                        value: formData?.user?.post_office || "",
                      },
                      {
                        label: "District",
                        name: "district",
                        value: formData?.user?.district || "",
                      },
                      {
                        label: "State",
                        name: "state",
                        value: formData?.user?.state || "",
                      },
                      {
                        label: "Date of Birth",
                        name: "dob",
                        type: "date",
                        value: formData?.user?.dob
                          ? formData?.user?.dob.slice(0, 10)
                          : "",
                      },
                      {
                        label: "Aadhar",
                        name: "aadhar_number",
                        value: formData?.user?.aadhar_number || "",
                      },
                      {
                        label: "PAN",
                        name: "pan_number",
                        value: formData?.user?.pan_number || "",
                      },
                    ].map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && (
                            <span className="text-red-500 ml-2">*</span>
                          )}
                        </label>
                        <input
                          type={field.type || "text"}
                          name={field.name}
                          value={field.value}
                          onChange={(e) => handleChange(e, "user")}
                          placeholder={field.label}
                          className={`${inputClass} ${field.readOnly ? "bg-gray-100" : ""}`}
                          readOnly={field.readOnly}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Company Details */}
                <div className="pb-4">
                  <h3 className="text-lg text-blue-500 font-semibold mb-2 flex items-center pt-4 gap-2">
                    <Building2 className="text-blue-500" size={18} />
                    Company Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-1">
                    {[
                      {
                        label: "Company Name",
                        name: "company_name",
                        value: formData?.company?.company_name || "",
                        required: true,
                      },
                      {
                        label: "Company Address",
                        name: "company_address",
                        value: formData?.company?.company_address || "",
                      },
                      {
                        label: "Company CIN",
                        name: "company_cin",
                        value: formData?.company?.company_cin || "",
                      },
                      {
                        label: "Company GST",
                        name: "company_gst_no",
                        value: formData?.company?.company_gst_no || "",
                      },
                      {
                        label: "Company PAN",
                        name: "company_pan",
                        value: formData?.company?.company_pan || "",
                      },
                      {
                        label: "Authorized Person",
                        name: "authorized_person_name",
                        value: formData?.company?.authorized_person_name || "",
                      },
                    ].map((field, index) => (
                      <div key={index}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {field.required && (
                            <span className="text-red-500 ml-2">*</span>
                          )}
                        </label>
                        <input
                          type="text"
                          name={field.name}
                          value={field.value}
                          onChange={(e) => handleChange(e, "company")}
                          placeholder={field.label}
                          className={inputClass}
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Type
                        <span className="text-red-500 ml-2">*</span>
                      </label>
                      <select
                        name="company_type_name"
                        value={formData.company?.company_type_name || ""}
                        onChange={(e) => handleChange(e, "company")}
                        className={inputClass}
                      >
                        <option value="" disabled selected>
                          select comp Type
                        </option>
                        <option value="Private Limited">Private Limited</option>
                        <option value="Public Limited">Public Limited</option>
                        <option value="NGO Sec-8">NGO Sec-8</option>
                        <option value="OPC">OPC</option>
                        <option value="LLP">LLP</option>
                        <option value="Partnership Firm">
                          Partnership Firm
                        </option>
                        <option value="Individual">Individual</option>
                        <option value="Other's">Other's</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Selected Company Type
                      </label>
                      <select
                        // name="company_type_name"
                        value={formData.company?.company_type?.name || ""}
                        onChange={(e) => handleChange(e, "company")}
                        className="border rounded-md p-2 w-full border-blue-600 bg-slate-500 text-white"
                        disabled // This disables the dropdown, making it read-only.
                      >
                        <option value="" disabled>
                          Select Company Type
                        </option>
                        <option value="Private Limited">Private Limited</option>
                        <option value="Public Limited">Public Limited</option>
                        <option value="NGO Sec-8">NGO Sec-8</option>
                        <option value="OPC">OPC</option>
                        <option value="LLP">LLP</option>
                        <option value="Partnership Firm">
                          Partnership Firm
                        </option>
                        <option value="Individual">Individual</option>
                        <option value="Other's">Other's</option>
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )
          )}
          <div className="flex items-center gap-1.5 text-xs text-rose-600">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>
              Fields marked with{" "}
              <span className="text-rose-500 font-semibold">*</span> are
              required
            </span>
          </div>{" "}
        </div>
        {/* Modal Footer */}
        <div className="flex justify-end gap-3 p-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          {/* Cancel Button - Neutral/Outline Style */}
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>

          {/* Save Button - Primary Style with Loading State */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCustomerModal;
